import type { ClubPermission } from '@/core/models/staff'
import type { ClubRole } from '@/core/models/club-role'

/**
 * Effective permission check for the current user.
 *
 * Resolution order:
 * 1. Look up the user's `StaffMember.role` (a string like 'owner' or a custom role ID).
 * 2. Load the corresponding `ClubRole` document.
 * 3. Return whether the role has the given permission.
 *
 * Owner is always treated as omnipotent regardless of what's in the role doc —
 * guards against accidental permission stripping. Mirrors 's
 * `CurrentUserRole` + `roleCan` logic.
 */
export function can(role: ClubRole | null, permission: ClubPermission): boolean {
  if (role == null) return false
  if (role.id === 'owner') return true
  return role.permissions.includes(permission)
}

/**
 * Coarse role tier for quick checks (route guards, sidebar filtering). Returns
 * one of 'owner' | 'manager' | 'staff'. Custom roles fall under 'staff' unless
 * their priority says otherwise.
 */
export type RoleTier = 'owner' | 'manager' | 'staff'

export function roleTier(role: ClubRole | null): RoleTier | null {
  if (role == null) return null
  if (role.id === 'owner') return 'owner'
  if (role.id === 'manager') return 'manager'
  // Custom roles: priority 0 → owner, 1 → manager, else staff.
  if (role.priority === 0) return 'owner'
  if (role.priority === 1) return 'manager'
  return 'staff'
}

/**
 * Whether `tier` satisfies a minimum-rank requirement.
 * requires('owner') → only owner
 * requires('manager') → owner OR manager
 * requires('staff') → anyone authenticated
 */
export function tierMeets(tier: RoleTier | null, required: RoleTier): boolean {
  if (tier == null) return false
  if (required === 'staff') return true
  if (required === 'manager') return tier === 'owner' || tier === 'manager'
  return tier === 'owner'
}
